import { useEffect } from 'preact/hooks';
import { Pinned } from './Chrome';
import { GIFT_LEGAL, To, WHAT_IT_PAYS_FOR } from './TrustPages';
import './support.css';

// Support Us (README section 6), under Dan's decisions:
//   D3  donations OPENED 28 Sep 2026 through Dan's Stripe Payment Link, the donor choosing the
//       amount on Stripe's own page: this page links to it and loads nothing from Stripe, so the
//       site stays free of outside scripts and cookies. No amount chips (the amount is chosen on
//       Stripe's page, and check-money keeps dollar figures out of the site's copy). The legal
//       line under the button is Dan's approved wording (GIFT_LEGAL);
//   T7  v1 has no State pages; the States menu stays, and every state in it opens this page
//       at its #states section, which says what a state needs before it is built;
//   T9  no cost figure anywhere on the site;
//   T10 nothing about donations is listed on the site (no donor, amount or total); private
//       citizens are preferred, nobody is claimed to be blocked, and no gift affects the record.
//
// The prototype's copy said the site "is paid for by the people who read it". Today it is
// not: one person pays (D2). Every sentence here is true on the day it is read, or says
// when it will become true.

// Dan's Stripe Payment Link ("Customers choose what to pay"). A plain link: nothing reaches Stripe
// until a visitor follows it. check-hosts.mjs lists buy.stripe.com.
// Replaced the first link the same day, 28 Sep 2026: that one had Stripe's automatic tax on.
export const DONATE_URL = 'https://buy.stripe.com/4gMdR995s5uB9BQ1XWg7e02';

const RULES = [
  'Gifts from private citizens are preferred. The site cannot stop a party, a campaign, a member of Congress or a lobbyist from giving, and does not claim to.',
  'No ads. An ad network sets its own tracking cookies, and a political ad beside a vote record reads as an endorsement.',
  'Giving buys nothing. Every gift is recorded the same way, whoever it comes from; none changes what the site shows or how it reports the record; no donor sees a summary before it publishes, and no correction moves faster for a donor.',
  'Nothing about you is stored beside what you read. There are no visitor accounts and no analytics trackers.',
  'Donors are not listed on this site, by name, by amount or in total, so no gift earns anyone recognition here.',
];

// The design's own list (the state page's "What has to be true before it is built").
const STATE_CHECKS: [string, string][] = [
  ['Recorded votes on final passage',
    'Some chambers decide most bills by voice. Where no names are recorded, every cell in that column can only read Voice vote, and a grid of voice votes answers nothing.'],
  ['Service history reliable enough to rank by',
    'Rows are ordered by time in office. That ordering needs dependable start and end dates for past terms, including members who moved between chambers.'],
  ['A source that stays put',
    'Much state vote data is gathered from state websites rather than published as data. Format and coverage change without notice, and the nightly job has to survive that.'],
];

export function SupportPage() {
  // Arriving from the States menu, or from a link to /support#states, lands on that section.
  useEffect(() => {
    if (location.hash === '#states') document.getElementById('states')?.scrollIntoView({ block: 'start' });
  }, []);

  return (
    <Pinned class="sr-support-wrap">
      <div class="sr-support">
        <div class="sr-support-col">
          <h2>Support Us</h2>
          <p class="sr-support-p">Dan Campbell, who runs the site, pays for it personally, and gifts help. Gifts from
            private citizens are the ones the site hopes for. There is no membership, no login, and nothing to
            cancel.</p>
          <p class="sr-support-p">It does not take much. The whole site is a nightly job that collects the official
            record and a small server that hands it out, so the list of what it pays for is short, and it stays
            short.</p>

          <h3 class="sr-support-label">Donations</h3>
          <div class="sr-support-give">
            <a class="btn btn-primary sr-support-give-btn" href={DONATE_URL} rel="noreferrer">Give through Stripe</a>
            <p>You choose the amount on Stripe’s own page, which handles the payment. This site never sees your card
              or anything you type there; the <To path="/privacy">Privacy</To> page says what Stripe keeps. Gifts are
              one-time, and they are not listed on this site, by name or by amount.</p>
            <p class="sr-support-legal">{GIFT_LEGAL}</p>
          </div>
          <p class="sr-support-note">How the site is paid for, and why donors are not listed, is
            on <To path="/who-pays">Who pays for this</To>.</p>
        </div>

        <div class="sr-support-col">
          <h3 class="sr-support-label">What it pays for</h3>
          <ul class="sr-support-list">
            {WHAT_IT_PAYS_FOR.map((t) => <li key={t}><span>{t}</span></li>)}
          </ul>

          <h3 class="sr-support-label sr-support-gap">Rules this money follows</h3>
          {RULES.map((t) => <div key={t} class="sr-support-rule">{t}</div>)}
        </div>
      </div>

      <section id="states" class="sr-support sr-support-states">
        <div class="sr-support-col">
          <div class="sr-support-kicker">State legislatures</div>
          <h2>The same grid, one state at a time</h2>
          <p class="sr-support-p">The States menu lists all fifty, and none is built yet. When one is, it will be this
            same grid under the same rules: every member of the legislature down the left, ranked by time in office,
            and how each one voted on every recorded item across the top.</p>
          <p class="sr-support-p">State records are the next thing support would pay for. Nothing here is a promise
            about a date: a state is built when it meets the three conditions on this page, and not before.</p>
          <p class="sr-support-note">Source when built: <a href="https://openstates.org" rel="noreferrer">Open
            States</a>, which collects bills, legislators and votes for all fifty states. Much of it is gathered from
            state websites, so coverage varies by state and by chamber. When a state is ready, it appears in the menu
            without “Coming soon”.</p>
        </div>
        <div class="sr-support-col">
          <h3 class="sr-support-label">What has to be true before a state is built</h3>
          {STATE_CHECKS.map(([title, body]) => (
            <div key={title} class="sr-support-check">
              <div class="sr-support-check-title">{title}</div>
              <div class="sr-support-check-body">{body}</div>
            </div>
          ))}
        </div>
      </section>
    </Pinned>
  );
}

/** Any address the site does not have. It says so, rather than drawing the grid as if it had
 *  been asked for. */
export function NotFound({ path }: { path: string }) {
  return (
    <Pinned class="sr-support-wrap">
      <div class="sr-notfound">
        <h2>No page at this address</h2>
        <p class="sr-support-p">Nothing on this site lives at <code>{path}</code>. The record itself is on the front
          page, and every item and member has its own address from there.</p>
        <To path="/">← Back to the record</To>
      </div>
    </Pinned>
  );
}
